export const WORKLET_URL = "/pcm-worklet.js";
export const WORKLET_NAME = "pcm16-downsampler";

interface MicSourceOptions {
  /** Defaults to the worklet's 16kHz (transcription); the voice-agent socket needs 24kHz. */
  targetRate?: number;
  requireEchoCancellation?: boolean;
  /** The worklet only decimates, so must be >= `targetRate`; omitted = browser default. */
  contextSampleRate?: number;
}

interface PcmSourceHandlers {
  emit: (chunk: ArrayBuffer) => void;
  // Finite sources wait on this so no audio precedes the socket; the mic ignores it and drops pre-open chunks.
  whenReady: Promise<void>;
  // The mic never ends.
  onEnded?: () => void;
}

export interface PcmSource {
  start(handlers: PcmSourceHandlers): Promise<void>;
  stop(): void;
}

/** Distinguishes a local-capture failure from a socket failure awaited in the same `start()`. */
export class MicrophoneError extends Error {}

function micErrorMessage(e: unknown): string {
  const name = e instanceof DOMException ? e.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access is blocked. Allow mic permission in your browser, then try again.";
    case "NotFoundError":
      return "No microphone found. Connect a mic and try again.";
    case "NotReadableError":
      return "Your microphone is in use by another app. Close it and try again.";
    default:
      return "I'm not picking up any audio. Check that your mic is connected and try again.";
  }
}

export function createMicSource(options: MicSourceOptions = {}): PcmSource {
  let ctx: AudioContext | null = null;
  let stream: MediaStream | null = null;
  let worklet: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;

  return {
    async start({ emit }) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: options.requireEchoCancellation
              ? { exact: true }
              : true,
            noiseSuppression: true,
          },
        });
      } catch (e) {
        throw new MicrophoneError(micErrorMessage(e));
      }
      if (
        options.requireEchoCancellation &&
        stream.getAudioTracks()[0]?.getSettings().echoCancellation !== true
      ) {
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
        throw new MicrophoneError(
          "This browser couldn't enable echo cancellation. Please try another browser.",
        );
      }
      ctx = new AudioContext(
        options.contextSampleRate
          ? { sampleRate: options.contextSampleRate }
          : undefined,
      );
      await ctx.audioWorklet.addModule(WORKLET_URL);
      source = ctx.createMediaStreamSource(stream);
      worklet = new AudioWorkletNode(ctx, WORKLET_NAME, {
        processorOptions: { targetRate: options.targetRate },
      });
      worklet.port.onmessage = (ev) => emit(ev.data as ArrayBuffer);
      source.connect(worklet);
    },
    stop() {
      worklet?.disconnect();
      source?.disconnect();
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close().catch(() => {});
      worklet = source = stream = ctx = null;
    },
  };
}
